import { ApprovalFlowStep } from '@app/components/approval-status-flow/approval-status-flow.component';
import { ApprovalFlowStatus } from '@app/enums';
import { formatDate } from '@app/functions/date-format';
import { SignatureRequest } from '@app/models/documents/signature-request.model';
import { OfferLetter } from '@app/models/offer-letters/offer-letter.model';
import { Translatable } from '@app/types/translatable.type';

export function createCosignerApprovalFlowSteps(offerLetter: OfferLetter, currentLang: string): ApprovalFlowStep[] {
    const firstStep = {
        title: 'offer-letter.offerLetterSentToCosigner',
        description: {
            key: 'offer-letter.sentOn',
            params: { date: formatDate(offerLetter.createdAt, 'short', currentLang) },
        },
        status: ApprovalFlowStatus.PositiveStatus,
        hideIndicator: false,
    };

    const cosigners = offerLetter.cosigners;

    const hasOrder = cosigners.some((cosigner) => cosigner.order);

    if (hasOrder) {
        cosigners.sort((a: SignatureRequest, b: SignatureRequest) => a.order - b.order);
    }

    // For the cosigners
    return [
        firstStep,
        ...cosigners.map((signatureRequestCosigner, index) => {
            const previousValue = index > 0 ? cosigners[index - 1] : null; // null or any default value

            return {
                title: signatureRequestCosigner.employee.fullName,
                description: descriptionCreator(signatureRequestCosigner, previousValue, hasOrder, currentLang),
                status: signatureRequestCosigner.isSigned() ? ApprovalFlowStatus.PositiveStatus : undefined,
                hideIndicator: false,
                buttonText:
                    descriptionCreator(signatureRequestCosigner, previousValue, hasOrder, currentLang) ===
                    'offer-letter.waitingForSigning'
                        ? 'remind'
                        : undefined,
                data: signatureRequestCosigner,
            };
        }),
    ];
}

function descriptionCreator(
    signatureRequest: SignatureRequest | null,
    previousSignatureRequest: SignatureRequest | null,
    hasOrder: boolean,
    currentLang: string
): Translatable {
    if (!signatureRequest) {
        return '';
    }

    // If the cosigner has signed then just return the signature date
    if (signatureRequest.isSigned()) {
        return {
            key: 'offer-letter.signedOn',
            params: { date: formatDate(signatureRequest.completedAt, 'short', currentLang) },
        };
    }

    if (hasOrder) {
        // This is for the first cosigner
        if (signatureRequest.order === 1 && !previousSignatureRequest?.isSigned()) {
            return `offer-letter.waitingForSigning`;
        }

        // This will be null if it's the first cosigner
        if (previousSignatureRequest?.isSigned()) {
            return `offer-letter.waitingForSigning`;
        }

        return `pending`;
    }

    return `offer-letter.waitingForSigning`;
}
